// ─────────────────────────────────────────────────────────────────────────
// subcontractReportPdfRenderer.js
//
// Renders a complete, standalone A4 print document from the SAME rows[]
// and summary{} objects buildSubcontractReportRows()/buildExecutiveSummary()
// (subcontractReportData.js) produce for the on-screen React component.
//
// Architectural contract (per the approved decision):
//   Report Data (subcontractReportData.js — SSOT, unchanged)
//        ↓                              ↓
//   Screen Renderer                Print Renderer (this file)
//   (SubcontractReport.jsx,        (pure function, HTML string out,
//    React, DOM, on-screen)         no React, no DOM, no calculation)
//
// This function NEVER recalculates a financial value — every number
// printed here is read directly from a row/summary field already
// computed by subcontractReportData.js. It also never touches or
// clones any live DOM node — the previous implementation's root cause
// (copying a rendered Modal's outerHTML) is deliberately not repeated;
// this is a plain string-building function, same pattern as the
// existing, proven AnalyticsReportPdfRenderer.js.
// ─────────────────────────────────────────────────────────────────────────

const BRAND = {
  navy: '#0F1017', blue: '#1B3A6B', blueLt: '#5DADE2',
  green: '#1A7A4A', greenLt: '#F0FDF4',
  red: '#DC2626', redLt: '#FEF2F2',
  amber: '#D97706', amberLt: '#FFFBEB',
  blueChip: '#1D4ED8', blueChipLt: '#EFF6FF',
  gray1: '#F9FAFB', gray2: '#F3F4F6', border: '#E5E7EB',
  text1: '#0F1017', text2: '#374151', text3: '#6B7280', text4: '#9CA3AF',
}

const STATUS_LABEL = { draft: 'Draft', approved: 'Approved', closed: 'Closed', cancelled: 'Cancelled' }
const STATUS_COLOR = {
  draft: { bg: BRAND.gray2, color: BRAND.text3 },
  approved: { bg: BRAND.greenLt, color: BRAND.green },
  closed: { bg: BRAND.blueChipLt, color: BRAND.blueChip },
  cancelled: { bg: BRAND.redLt, color: BRAND.red },
}

export function fmt(n) {
  if (n == null) return '—'
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n)
}
function fmtM(n) {
  if (n == null) return '—'
  const m = (n || 0) / 1e6
  return Math.abs(m) >= 1 ? m.toFixed(1) + 'M' : fmt(n)
}
export function fmtDate(d) {
  return d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'
}
export function today() {
  return new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })
}
export function monthYear() {
  return new Date().toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
}
export function escapeHtml(str) {
  if (str == null) return ''
  return String(str)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#039;')
}

// ─── Section builders — each returns a self-contained HTML fragment ───────
// Every section is its own natural block in document flow (no
// position:fixed/absolute anywhere) — the browser's own print engine
// paginates them; long tables are simply left as long <table> elements
// with `thead` marked to repeat (see CSS below), which lets a table
// cross page boundaries naturally instead of being forced into one
// fixed-size container.

function reportHeaderSection(project, meta) {
  return `
  <section class="report-header">
    <div class="accent-bar"></div>
    <div class="header-top">
      <div class="brand">
        ${logoSvg(30)}
        <div>
          <div class="brand-name">Cost<span class="brand-accent">Space</span></div>
          <div class="brand-tagline">ENTERPRISE COST INTELLIGENCE</div>
        </div>
      </div>
      <div class="header-title-block">
        <div class="header-kicker">COMMERCIAL / COST CONTROL SUBCONTRACT REPORT</div>
        <div class="header-title">${escapeHtml(project?.name || '')}</div>
      </div>
      <div class="header-date">${today()}</div>
    </div>
    <div class="header-meta-row">
      <div class="header-meta-item"><span class="header-meta-label">PERIOD</span><span class="header-meta-value">${monthYear()}</span></div>
      <div class="header-meta-item"><span class="header-meta-label">SCOPE</span><span class="header-meta-value">${meta.rowCount} (${meta.materialCount} Material &middot; ${meta.equipmentCount} Equipment)</span></div>
      <div class="header-meta-item"><span class="header-meta-label">TYPES</span><span class="header-meta-value">${escapeHtml(meta.contractTypesLabel)}</span></div>
      <div class="header-meta-item"><span class="header-meta-label">STATUS</span><span class="header-meta-value">${escapeHtml(meta.statusLabel)}</span></div>
      <div class="header-meta-item confidential">&#128274; Strictly Confidential</div>
    </div>
  </section>`
}

function executiveSummarySection(summary) {
  const kpis = [
    { label: 'TOTAL REVISED VALUE', value: fmtM(summary.totalRevisedValue), cls: 'kpi-neutral' },
    { label: 'TOTAL VARIATIONS', value: fmtM(summary.totalVariations), cls: 'kpi-blue' },
    { label: 'TOTAL CERTIFIED', value: fmtM(summary.totalCertified), cls: 'kpi-green' },
    { label: 'GROSS RETENTION', value: fmtM(summary.totalRetentionGross), cls: 'kpi-amber' },
    { label: 'RETENTION RELEASED', value: fmtM(summary.totalRetentionReleased), cls: 'kpi-blue' },
    { label: 'NET RETENTION HELD', value: fmtM(summary.totalRetentionHeld), cls: 'kpi-amber' },
    { label: 'TOTAL PAID', value: fmtM(summary.totalPaid), cls: 'kpi-blue' },
    { label: 'TOTAL OUTSTANDING', value: fmtM(summary.totalOutstanding), cls: summary.totalOutstanding > 0 ? 'kpi-red' : 'kpi-neutral' },
    { label: 'TOTAL REMAINING COMMITMENT', value: fmtM(summary.totalRemainingCommitment), cls: 'kpi-neutral' },
  ]
  return `
  <section class="section-block">
    <div class="header-lead-in">
      ${sectionHeader('Executive Summary')}
      <div class="kpi-grid">
        ${kpis.map(k => `<div class="kpi-card ${k.cls}"><div class="kpi-label">${k.label}</div><div class="kpi-value">SAR ${k.value}</div></div>`).join('')}
      </div>
    </div>
  </section>`
}

function contractSummarySection(rows, summary) {
  return `
  <section class="section-block">
    ${sectionHeader('All Contracts — Summary')}
    <table class="data-table">
      <colgroup>
        <col style="width:4%"><col style="width:19%"><col style="width:6%">
        <col style="width:10%"><col style="width:10%"><col style="width:10%">
        <col style="width:10%"><col style="width:10%"><col style="width:10%"><col style="width:11%">
      </colgroup>
      <thead>
        <tr>
          <th class="al">Type</th><th class="al">Contractor</th><th class="al">Contract No.</th>
          <th>Revised Value</th><th>Certified</th><th>Net Retention</th><th>Paid</th><th>Outstanding</th><th>Remaining</th><th class="al">Status</th>
        </tr>
      </thead>
      <tbody>
        ${rows.map(r => `
        <tr>
          <td class="al">${r.contract_type === 'equipment' ? '&#128666;' : '&#128230;'}</td>
          <td class="al contractor-cell">${escapeHtml(r.contractor_name)}</td>
          <td class="al mono">${escapeHtml(r.contract_no)}</td>
          <td class="mono">${fmt(r.revised_value)}</td>
          <td class="mono green">${fmt(r.certified)}</td>
          <td class="mono amber">${fmt(r.retention_held)}</td>
          <td class="mono blue">${fmt(r.paid)}</td>
          <td class="mono ${r.outstanding > 0 ? 'red' : ''}">${fmt(r.outstanding)}</td>
          <td class="mono">${fmt(r.remaining_commitment)}</td>
          <td class="al"><span class="status-chip" style="background:${STATUS_COLOR[r.status]?.bg};color:${STATUS_COLOR[r.status]?.color}">${STATUS_LABEL[r.status] || r.status}</span></td>
        </tr>`).join('')}
      </tbody>
      <tfoot>
        <tr class="totals-row">
          <td colspan="3">Grand Total (${rows.length})</td>
          <td class="mono">${fmt(summary.totalRevisedValue)}</td>
          <td class="mono green">${fmt(summary.totalCertified)}</td>
          <td class="mono amber">${fmt(summary.totalRetentionHeld)}</td>
          <td class="mono blue">${fmt(summary.totalPaid)}</td>
          <td class="mono ${summary.totalOutstanding > 0 ? 'red' : ''}">${fmt(summary.totalOutstanding)}</td>
          <td class="mono">${fmt(summary.totalRemainingCommitment)}</td>
          <td></td>
        </tr>
      </tfoot>
    </table>
  </section>`
}

function variationsSection(rows) {
  const withVariations = rows.filter(r => r.variations.length > 0)
  if (withVariations.length === 0) {
    return `
    <section class="section-block">
      <div class="header-lead-in">
        ${sectionHeader('Variation Analysis — by Contract')}
        <div class="empty-note">No variations recorded for any contract in this scope.</div>
      </div>
    </section>`
  }
  const [firstV, ...restV] = withVariations
  return `
  <section class="section-block">
    <div class="header-lead-in">
      ${sectionHeader('Variation Analysis — by Contract')}
      ${variationBlock(firstV)}
    </div>
    ${restV.map(r => variationBlock(r)).join('')}
  </section>`
}

export function variationBlock(r) {
  return `
      <div class="variation-block">
        <div class="variation-block-header">
          <span>${r.contract_type === 'equipment' ? '&#128666;' : '&#128230;'} ${escapeHtml(r.contractor_name)} (${escapeHtml(r.contract_no)})</span>
          ${!r.variation_chain_reconciles ? '<span class="pending-chip">&#9888; Pending PO re-approval</span>' : ''}
        </div>
        <table class="data-table variation-table">
          <colgroup><col style="width:8%"><col style="width:14%"><col style="width:43%"><col style="width:17%"><col style="width:18%"></colgroup>
          <thead><tr><th class="al">#</th><th class="al">Date</th><th class="al">Description</th><th>Value Change</th><th>New Value</th></tr></thead>
          <tbody>
            <tr><td colspan="3" class="al">Original Contract Value</td><td></td><td class="mono bold">${fmt(r.original_value)}</td></tr>
            ${r.variations.map(v => `
            <tr>
              <td class="al blue">#${v.addendumNo}</td>
              <td class="al">${fmtDate(v.date)}</td>
              <td class="al">${escapeHtml(v.description)}</td>
              <td class="mono ${v.valueChange >= 0 ? 'green' : 'red'}">${v.valueChange >= 0 ? '+' : ''}${fmt(v.valueChange)}</td>
              <td class="mono">${fmt(v.newContractValue)}</td>
            </tr>`).join('')}
            <tr class="totals-row">
              <td colspan="3">${r.variation_chain_reconciles ? 'Revised Contract Value' : 'Revised (from chain — pending)'}</td>
              <td class="mono blue">${fmt(r.total_variations)}</td>
              <td class="mono bold">${fmt(r.variation_chain_expected_revised)}</td>
            </tr>
            ${!r.variation_chain_reconciles ? `<tr><td colspan="5" class="pending-note">&#9888; Current contract value on record is SAR ${fmt(r.revised_value)} — differs from the chain total because the linked PO for the latest addendum has not yet completed re-approval.</td></tr>` : ''}
          </tbody>
        </table>
      </div>`
}

function retentionSection(rows, summary) {
  return `
  <section class="section-block">
    ${sectionHeader('Retention — Gross / Released / Held')}
    <table class="data-table">
      <colgroup>
        <col style="width:26%"><col style="width:14%"><col style="width:15%">
        <col style="width:15%"><col style="width:15%"><col style="width:15%">
      </colgroup>
      <thead><tr><th class="al">Contractor</th><th class="al">Contract No.</th><th>Gross Retention</th><th>Released</th><th>Net Held</th><th class="center">Reconciles</th></tr></thead>
      <tbody>
        ${rows.map(r => {
          const checkOk = Math.abs((r.retention_gross - r.retention_released) - r.retention_held) < 0.01
          return `
        <tr>
          <td class="al contractor-cell">${r.contract_type === 'equipment' ? '&#128666;' : '&#128230;'} ${escapeHtml(r.contractor_name)}</td>
          <td class="al mono">${escapeHtml(r.contract_no)}</td>
          <td class="mono">${fmt(r.retention_gross)}</td>
          <td class="mono blue">${fmt(r.retention_released)}</td>
          <td class="mono amber bold">${fmt(r.retention_held)}</td>
          <td class="center">${checkOk ? '&#10003;' : '&#9888;'}</td>
        </tr>`}).join('')}
      </tbody>
      <tfoot>
        <tr class="totals-row">
          <td colspan="2">Grand Total</td>
          <td class="mono">${fmt(summary.totalRetentionGross)}</td>
          <td class="mono blue">${fmt(summary.totalRetentionReleased)}</td>
          <td class="mono amber">${fmt(summary.totalRetentionHeld)}</td>
          <td></td>
        </tr>
      </tfoot>
    </table>
    <div class="footnote">Gross Retention &minus; Released = Net Retention Held, verified per contract.</div>
  </section>`
}

function contractorDetailSection(rows) {
  if (rows.length === 0) {
    return `
    <section class="section-block">
      <div class="header-lead-in">
        ${sectionHeader('Contractor Detail')}
        <div class="empty-note">No contracts in this scope.</div>
      </div>
    </section>`
  }
  // Root cause fix (this revision): only the header + the FIRST card
  // are wrapped together in one break-inside:avoid unit
  // (.header-lead-in). Every subsequent card is free-flowing — this is
  // what lets Page 1 use its remaining space (header + first card can
  // still land there if they fit) while additional contractors flow
  // naturally onto Page 2+ instead of the entire section being forced
  // to jump as one block.
  const [first, ...rest] = rows
  return `
  <section class="section-block">
    <div class="header-lead-in">
      ${sectionHeader('Contractor Detail')}
      ${detailCard(first)}
    </div>
    ${rest.map(r => detailCard(r)).join('')}
  </section>`
}

/**
 * A single contractor's detail, organized into clearly labeled
 * commercial sub-sections instead of one flat 13-field grid — same
 * exact fields/values already computed by subcontractReportData.js,
 * only regrouped for readability. No new data is introduced.
 */
export function detailCard(r) {
  return `
      <div class="detail-card">
        <div class="detail-card-header">
          <span>${r.contract_type === 'equipment' ? '&#128666;' : '&#128230;'} ${escapeHtml(r.contractor_name)} (${escapeHtml(r.contract_no)})</span>
          <span class="status-chip" style="background:${STATUS_COLOR[r.status]?.bg};color:${STATUS_COLOR[r.status]?.color}">${STATUS_LABEL[r.status] || r.status}</span>
        </div>
        <div class="detail-subsections">
          <div class="detail-subsection">
            <div class="detail-subsection-title">Contract Information</div>
            <div class="detail-grid detail-grid-3">
              ${detailField('Contract Date', fmtDate(r.contract_date))}
              ${detailField('Completion Date', fmtDate(r.practical_completion_date))}
              ${detailField('DLP', r.dlp_months != null ? r.dlp_months + ' months' : '—')}
            </div>
          </div>
          <div class="detail-subsection">
            <div class="detail-subsection-title">Commercial Summary</div>
            <div class="detail-grid detail-grid-3">
              ${detailField('Original Value', 'SAR ' + fmt(r.original_value))}
              ${detailField('Variations', 'SAR ' + fmt(r.total_variations))}
              ${detailField('Revised Value', 'SAR ' + fmt(r.revised_value), true)}
            </div>
          </div>
          <div class="detail-subsection">
            <div class="detail-subsection-title">Certification / Payment</div>
            <div class="detail-grid detail-grid-3">
              ${detailField('Certified', 'SAR ' + fmt(r.certified), false, 'green')}
              ${detailField('Paid', 'SAR ' + fmt(r.paid), false, 'blue')}
              ${detailField('Outstanding', 'SAR ' + fmt(r.outstanding), false, r.outstanding > 0 ? 'red' : '')}
            </div>
          </div>
          <div class="detail-subsection">
            <div class="detail-subsection-title">Retention</div>
            <div class="detail-grid detail-grid-3">
              ${detailField('Gross Retention', 'SAR ' + fmt(r.retention_gross))}
              ${detailField('Retention Released', 'SAR ' + fmt(r.retention_released))}
              ${detailField('Net Retention Held', 'SAR ' + fmt(r.retention_held), false, 'amber')}
            </div>
          </div>
          <div class="detail-subsection detail-subsection-last">
            <div class="detail-subsection-title">Remaining Commitment</div>
            <div class="detail-grid detail-grid-3">
              ${detailField('Remaining Commitment', 'SAR ' + fmt(r.remaining_commitment), true)}
              ${r.variations.length > 0 ? detailField('Addendum Count', String(r.variations.length)) : ''}
            </div>
          </div>
        </div>
      </div>`
}

function detailField(label, value, bold, colorCls) {
  return `<div class="detail-field"><div class="detail-field-label">${label}</div><div class="detail-field-value ${bold ? 'bold' : ''} ${colorCls || ''}">${value}</div></div>`
}

export function sectionHeader(title) {
  return `
  <div class="section-header">
    <div class="section-header-left">${logoSvg(22)}<span class="section-brand">Cost<span class="brand-accent">Space</span></span><span class="dot">&middot;</span><span class="section-title">${title}</span></div>
    <div class="section-header-right">${monthYear()}</div>
  </div>`
}
// sectionFooter() removed (this revision) — was rendered once per
// section (redundant "Subcontract Report" repeated 5-6 times per
// document) and its page-number argument had no effect at all (the
// function ignored its parameter entirely). Replaced by ONE real,
// page-level footer using CSS Generated Content for Paged Media
// (see buildCss()'s @page rule below) — "CostSpace | Subcontract
// Report | Page X of Y" with a genuine, browser-computed page number,
// not a hardcoded string.

export function logoSvg(size) {
  return `<svg width="${size}" height="${size}" viewBox="0 0 100 100" style="flex-shrink:0">
    <rect width="100" height="100" rx="24" fill="${BRAND.blue}"/>
    <path d="M50 18 A32 32 0 1 0 82 50 L68 50 A18 18 0 1 1 50 32 Z" fill="#FFFFFF"/>
    <circle cx="73" cy="50" r="7.5" fill="${BRAND.blueLt}"/>
  </svg>`
}

// ─── CSS — A4 pagination is delegated entirely to the browser's own
// print engine via @page + natural document flow. No fixed heights, no
// position:fixed/absolute, no transform:scale — every section is a
// normal block-level element; `thead { display: table-header-group }`
// is what makes a long table's header repeat across pages, and
// `break-inside: avoid` on individual rows/cards (not whole sections)
// is what lets content flow naturally onto the next page rather than
// being clipped. Running headers/footers use CSS `@page` margin boxes
// where supported, with an in-flow fallback per-section footer for
// broader browser compatibility (Chrome's print-to-PDF, the primary
// target here, does not yet support @page margin-box content).
export function buildCss() {
  return `
    /* Real page footer via CSS Paged Media (CSS spec, no JavaScript,
       no hardcoded page numbers) — counter(page)/counter(pages) are
       computed by the browser's own print engine at print time.
       Known limitation, disclosed rather than silently worked around:
       Chrome's support for @page margin-box content is still partial
       as of this writing — it renders reliably in Chrome's own
       "Print > Save as PDF" flow (the primary path this renderer is
       built for, per every prior test in this session), but is not
       guaranteed identical in every browser/PDF engine. If a
       browser doesn't support it, the footer is simply absent for
       that browser — content is never lost, clipped, or duplicated. */
    @page {
      size: A4 portrait;
      margin: 12mm 10mm 16mm 10mm;
      @bottom-center {
        content: "CostSpace  |  Subcontract Report  |  Page " counter(page) " of " counter(pages);
        font-family: -apple-system, 'Segoe UI', 'Inter', Arial, sans-serif;
        font-size: 8px;
        color: #9CA3AF;
      }
    }
    * { box-sizing: border-box; }
    body { margin: 0; font-family: -apple-system, 'Segoe UI', 'Inter', Arial, sans-serif; color: ${BRAND.text1}; -webkit-print-color-adjust: exact; print-color-adjust: exact; font-size: 10.5px; }

    /* Page-break policy (root cause fix, this revision): the previous
       rule (.section-header + * with break-before: avoid) forced the
       header AND everything immediately after it as one indivisible
       unit — if that combined height didn't fit the remaining space on
       a page, the browser pushed the WHOLE thing to the next page,
       leaving large unused whitespace behind (exactly the reported
       Page-1/Page-2 problem). Fixed by scoping the "keep together" rule
       to ONLY the header + first content item (via .header-lead-in, a
       wrapper applied once per section — see sectionHeader() below),
       not to the header followed by an open-ended "everything after
       it" selector. Every subsequent item in the section is free to
       flow onto the current page normally. */
    .section-block + .section-block { margin-top: 10px; }
    .report-header + .section-block { margin-top: 10px; }
    .kpi-grid, .variation-block, .detail-card { break-inside: avoid; }
    .header-lead-in { break-inside: avoid; }

    .accent-bar { height: 3px; background: linear-gradient(90deg, ${BRAND.blue}, ${BRAND.blueLt}, ${BRAND.blue}); border-radius: 3px 3px 0 0; }

    /* Compact report header — replaces the old full-page cover. Same
       four meta fields (Period/Scope/Types/Status) plus the
       confidentiality notice, now a single dense strip instead of a
       whole A4 page, so real content starts immediately below it. */
    .report-header { background: ${BRAND.navy}; border-radius: 8px; overflow: hidden; color: #fff; }
    .header-top { padding: 12px 16px 10px; display: flex; align-items: center; gap: 16px; }
    .brand { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
    .brand-name { font-weight: 800; font-size: 13px; letter-spacing: .02em; }
    .brand-accent { color: ${BRAND.blueLt}; }
    .brand-tagline { color: rgba(255,255,255,.4); font-size: 7px; letter-spacing: .08em; }
    .header-title-block { flex: 1; border-left: 1px solid rgba(255,255,255,.12); padding-left: 16px; }
    .header-kicker { color: ${BRAND.green}; font-size: 8px; font-weight: 700; letter-spacing: .08em; margin-bottom: 2px; }
    .header-title { font-size: 16px; font-weight: 800; }
    .header-date { color: rgba(255,255,255,.6); font-size: 9px; font-family: 'SF Mono', Consolas, monospace; flex-shrink: 0; }
    .header-meta-row { display: flex; align-items: center; gap: 18px; padding: 8px 16px; border-top: 1px solid rgba(255,255,255,.08); background: rgba(255,255,255,.03); flex-wrap: wrap; }
    .header-meta-item { display: flex; align-items: baseline; gap: 5px; }
    .header-meta-label { color: rgba(255,255,255,.35); font-size: 7px; font-weight: 700; letter-spacing: .06em; }
    .header-meta-value { color: #fff; font-size: 9px; font-weight: 600; }
    .header-meta-item.confidential { margin-left: auto; color: rgba(255,255,255,.4); font-size: 8px; }

    .section-header { background: ${BRAND.navy}; padding: 7px 14px; display: flex; align-items: center; justify-content: space-between; border-radius: 6px 6px 0 0; }
    .section-header-left { display: flex; align-items: center; gap: 6px; }
    .section-brand { color: #fff; font-weight: 700; font-size: 9.5px; }
    .dot { color: rgba(255,255,255,.3); margin: 0 4px; }
    .section-title { color: rgba(255,255,255,.6); font-size: 9.5px; }
    .section-header-right { color: rgba(255,255,255,.4); font-size: 8.5px; }
    .section-block { border: 1px solid ${BRAND.border}; border-radius: 6px; overflow: hidden; }
    .section-block > .kpi-grid, .section-block > table, .section-block > .variation-block, .section-block > .detail-card, .section-block > .empty-note, .section-block > .footnote { margin: 12px 16px; }
    .section-block > .variation-block + .variation-block, .section-block > .detail-card + .detail-card { margin-top: 10px; }

    .kpi-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
    .kpi-card { border-radius: 6px; padding: 9px 11px; border: 1px solid rgba(0,0,0,.06); }
    .kpi-neutral { background: ${BRAND.gray1}; }
    .kpi-blue { background: ${BRAND.blueChipLt}; }
    .kpi-green { background: ${BRAND.greenLt}; }
    .kpi-amber { background: ${BRAND.amberLt}; }
    .kpi-red { background: ${BRAND.redLt}; }
    .kpi-label { font-size: 8px; font-weight: 600; letter-spacing: .03em; color: ${BRAND.text3}; margin-bottom: 3px; text-transform: uppercase; }
    .kpi-value { font-size: 13px; font-weight: 800; font-family: 'SF Mono', Consolas, monospace; }

    /* Table typography raised to the minimum genuinely legible size
       for a printed A4 report (9.5px body / 8px header) — the previous
       8.5px/7.5px pass was too aggressive, per the reported issue.
       colgroup column widths (set per-table in each render function)
       remain the fix for horizontal overflow — reducing font size was
       never the correct tool for that problem. */
    table.data-table { width: 100%; border-collapse: collapse; font-size: 9.5px; table-layout: fixed; }
    .data-table thead { display: table-header-group; }
    .data-table th { background: ${BRAND.gray1}; text-align: right; padding: 6px 7px; font-weight: 700; color: ${BRAND.text3}; font-size: 8px; text-transform: uppercase; border-bottom: 1px solid ${BRAND.border}; overflow-wrap: break-word; }
    .data-table th.al, .data-table td.al { text-align: left; }
    .data-table td { padding: 6px 7px; border-bottom: 1px solid ${BRAND.gray2}; overflow-wrap: break-word; }
    .data-table tr { break-inside: avoid; }
    .data-table .mono { font-family: 'SF Mono', Consolas, monospace; text-align: right; }
    .data-table .center { text-align: center; }
    .data-table .contractor-cell { font-weight: 600; overflow-wrap: break-word; }
    .data-table .green { color: ${BRAND.green}; }
    .data-table .blue { color: ${BRAND.blueChip}; }
    .data-table .amber { color: ${BRAND.amber}; }
    .data-table .red { color: ${BRAND.red}; }
    .data-table .bold { font-weight: 800; }
    .data-table .totals-row { background: ${BRAND.greenLt}; border-top: 2px solid rgba(26,122,74,.2); font-weight: 800; }
    .status-chip { display: inline-block; padding: 2px 7px; border-radius: 8px; font-size: 7.5px; font-weight: 700; white-space: nowrap; }

    .variation-block { border: 1px solid ${BRAND.border}; border-radius: 6px; overflow: hidden; }
    .variation-block-header { background: rgba(26,122,74,.08); padding: 7px 11px; display: flex; justify-content: space-between; font-weight: 700; font-size: 9.5px; }
    .pending-chip { font-size: 8px; font-weight: 700; color: ${BRAND.amber}; background: ${BRAND.amberLt}; padding: 2px 7px; border-radius: 8px; }
    .pending-note { padding: 6px 9px; font-size: 8px; color: ${BRAND.amber}; background: ${BRAND.amberLt}; }
    .empty-note { color: ${BRAND.text4}; font-size: 10px; text-align: center; padding: 20px 0; }
    .footnote { font-size: 8.5px; color: ${BRAND.text3}; }

    /* Contractor Detail: back to a 4-column grid (from the over-dense
       6-column pass) — this is the balance point between "compact" and
       "legible" the feedback asked for. Cards no longer force a page
       break, so several still fit on one sheet where 6 columns did;
       the difference is font size and field spacing, not layout
       aggressiveness. */
    /* Contractor Detail — redesigned this revision from one flat
       13-field grid into clearly labeled commercial sub-sections
       (Contract Information / Commercial Summary / Certification &
       Payment / Retention / Remaining Commitment), per the "make it
       look like a real commercial report" requirement. Cards remain
       full-width, one per row — a two-column layout was considered,
       but would have re-introduced the exact header/first-card
       coupling problem this revision fixes (the header-lead-in wrapper
       needs to bind cleanly to ONE first card, not a row of two). Each
       card is now substantially shorter than the old flat-grid version
       (organized sub-sections vs. one dense 13-cell block), so several
       contractors still flow naturally down a page without the
       complexity of a grid layout. */
    .detail-card { border: 1px solid ${BRAND.border}; border-radius: 6px; padding: 10px 12px; background: ${BRAND.gray1}; break-inside: avoid; }
    .detail-card-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; font-weight: 800; font-size: 10px; gap: 6px; }
    .detail-card-header > span:first-child { overflow-wrap: break-word; }
    .detail-subsections { display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px 14px; }
    .detail-subsection-last { grid-column: 1 / -1; }
    .detail-subsection-title { font-size: 7.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; color: ${BRAND.blueChip}; border-bottom: 1px solid ${BRAND.border}; padding-bottom: 2px; margin-bottom: 4px; }
    .detail-grid { display: grid; gap: 6px 8px; font-size: 9px; }
    .detail-grid-3 { grid-template-columns: repeat(3, 1fr); }
    .detail-field-label { color: ${BRAND.text4}; font-size: 6.5px; text-transform: uppercase; letter-spacing: .02em; margin-bottom: 1px; }
    .detail-field-value { font-family: 'SF Mono', Consolas, monospace; font-weight: 600; overflow-wrap: break-word; }
    .detail-field-value.bold { font-weight: 800; }
    .detail-field-value.green { color: ${BRAND.green}; }
    .detail-field-value.blue { color: ${BRAND.blueChip}; }
    .detail-field-value.amber { color: ${BRAND.amber}; }
    .detail-field-value.red { color: ${BRAND.red}; }
  `
}

/**
 * @param {Array} rows - buildSubcontractReportRows() output, UNCHANGED
 * @param {Object} summary - buildExecutiveSummary() output, UNCHANGED
 * @param {{project, contractTypes, statuses, contractorIds, sections}} meta
 * @returns {string} complete standalone HTML document
 */
export function renderSubcontractReportPdf(rows, summary, meta) {
  const { project, contractTypes, statuses, contractorIds, sections } = meta
  const materialCount = rows.filter(r => r.contract_type === 'material').length
  const equipmentCount = rows.filter(r => r.contract_type === 'equipment').length
  const coverMeta = {
    rowCount: rows.length, materialCount, equipmentCount,
    contractTypesLabel: contractTypes.map(t => t === 'material' ? 'Material' : 'Equipment').join(', '),
    statusLabel: statuses.map(s => STATUS_LABEL[s] || s).join(', '),
  }

  const sectionsHtml = [
    reportHeaderSection(project, coverMeta),
    sections.executiveSummary ? executiveSummarySection(summary) : '',
    sections.contractSummary ? contractSummarySection(rows, summary) : '',
    sections.variations ? variationsSection(rows) : '',
    sections.retention ? retentionSection(rows, summary) : '',
    sections.contractorDetails ? contractorDetailSection(rows) : '',
  ].filter(Boolean).join('\n')

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>Subcontract Report — ${escapeHtml(project?.name || '')}</title>
<style>${buildCss()}</style>
</head>
<body>
${sectionsHtml}
</body>
</html>`
}
